import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
    ProtoResponse
} from "../../../api/generated/APIClient";
import { AdminClient } from "../../../api/generated/APIClient";
import { generateUUIDv7 } from "../../../utils/uuid";
import "./UserCreateForm.css";

interface UserCreateFormProps {
  onSuccess?: (user: any) => void;
  onCancel?: () => void;
}

export const UserCreateForm: React.FC<UserCreateFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フォームの状態
  const [formData, setFormData] = useState({
    name: "",
    is_debug_user: false,
    user_time: "",
    duration: 0,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("ユーザー名は必須です");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 新しいユーザーオブジェクトを作成（必要なフィールドを自動生成）
      const now = new Date();
      const userId = generateUUIDv7();

      // user_timeの処理
      let userTime: any | undefined = undefined;
      
      if (formData.user_time.trim()) {
        // FixedTime指定の場合：その時刻に固定
        try {
          const fixedTime = new Date(formData.user_time).toISOString();
          userTime = {
            fixed_time: new Date(fixedTime), // FixedTime（JSONフィールド名はfixed_time）
            duration: 0, // Duration=0でFixedTimeを使用
          };
        } catch (error) {
          setError("ユーザー時刻の形式が正しくありません");
          return;
        }
      } else if (formData.duration !== 0) {
        // Duration指定の場合：現在時刻+Duration
        userTime = {
          fixed_time: new Date(0), // FixedTime初期値（1970-01-01）
          duration: formData.duration, // 現在時刻からのオフセット秒数
        };
      }
      // 両方未指定の場合：userTime=undefinedでリアルタイム

      const newUser = {
        id: userId,
        name: formData.name.trim(),
        is_debug_user: formData.is_debug_user,
        create_date: now,
        update_date: now,
        version: "v1",
        user_time: userTime,
        migrate_version_bit: 0,
      };

      console.log("🔍 Frontend User Data (snake_case):", newUser);
      
      const args: any = {
        users: [newUser], // connect-clientが自動でcamelCaseに変換
        force_execute: false,
      };

      const response: ProtoResponse<any> =
        await AdminClient.CreateUser(args);

      if (response.error) {
        setError(response.error);
        console.error("ユーザー作成エラー:", response.error);
      } else {
        console.log("ユーザー作成成功:", response.data);

        if (onSuccess) {
          onSuccess(newUser);
        } else {
          // デフォルトではユーザー一覧ページに戻る
          navigate("/users");
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "ユーザーの作成に失敗しました";
      setError(errorMessage);
      console.error("ユーザー作成エラー:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/users");
    }
  };

  return (
    <div className="user-create-form">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            ユーザー名 <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="form-input"
            placeholder="ユーザー名を入力してください"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="user_time" className="form-label">
            ユーザー時刻設定
          </label>
          <input
            type="datetime-local"
            id="user_time"
            name="user_time"
            value={formData.user_time}
            onChange={handleInputChange}
            className="form-input"
            disabled={loading}
          />
          <small className="form-help">
            空の場合はリアルタイム。設定した場合はその時刻に固定されます。
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="duration" className="form-label">
            時刻オフセット（秒）
          </label>
          <input
            type="number"
            id="duration"
            name="duration"
            value={formData.duration || ''}
            onChange={handleInputChange}
            className="form-input"
            disabled={loading}
            placeholder="0"
          />
          <small className="form-help">
            固定時刻が空の場合のみ有効。現在時刻からのオフセット秒数。
          </small>
        </div>

        <div className="form-group">
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              name="is_debug_user"
              checked={formData.is_debug_user}
              onChange={handleInputChange}
              className="form-checkbox"
              disabled={loading}
            />
            <span className="checkbox-text">デバッグユーザー</span>
          </label>
          <small className="form-help">
            デバッグ用のユーザーとして作成する場合はチェックしてください
          </small>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            キャンセル
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "作成中..." : "ユーザーを作成"}
          </button>
        </div>
      </form>
    </div>
  );
};

